import { createFileRoute, useRouter } from "@tanstack/react-router";
import React, { Suspense, useEffect } from "react";
import { useTranslation } from "react-i18next";
import HeadingLayout from "src/components/base/heading-layout";
import {
    ComputerDesktopIcon,
    MagnifyingGlassIcon,
    RectangleGroupIcon,
    ServerStackIcon,
    SignalIcon,
    Squares2X2Icon,
} from "@heroicons/react/20/solid";
import { ApiInternal } from "src/api/api_internal";
import { EmptyState } from "src/components/base/empty-state";
import { FullDevice } from "src/api/generated/frontend_internal_v1";
import { Input, InputGroup } from "src/components/base/input";
import { Listbox, ListboxLabel, ListboxOption } from "src/components/base/listbox";
import RenameDeviceDialog from "src/components/dialogs/rename-device";
import ExpireDeviceKeyDialog from "src/components/dialogs/expire-device-key";
import DeleteDeviceDialog from "src/components/dialogs/delete-device";
import WS from "src/api/ws";
import { PrimaryButton } from "src/components/base/button";
import { FilterTile, FilterTileGroup } from "src/components/base/filter-tile";
import { useDeviceColumns } from "src/components/device-row-admin";
import { DataTable, useDataTable } from "src/components/data-table";
import { ToggleButton } from "src/components/toggle-button";
import HIGHLIGHT_CONTEXT from "src/context/highlight";
import { useForm } from "@tanstack/react-form";
import clsx from "clsx";

/**
 * The properties for {@link DeviceOverview}
 */
export type DeviceOverviewProps = {};

/**
 * Filter options
 */
const FILTERS = ["all", "server-only", "clients-only", "online-only"] as const;

/**
 * One of the {@link FILTERS} options
 */
type Filter = (typeof FILTERS)[number];

/**
 * An overview over all devices
 */
function DeviceOverview(props: DeviceOverviewProps) {
    const [t] = useTranslation("admin-device-overview");
    const [tg] = useTranslation();

    const router = useRouter();
    const navigate = Route.useNavigate();

    const { filter, search, page, tenant } = Route.useSearch();
    const { devices, tenants } = Route.useLoaderData();

    // Popups
    const [openRenameDevice, setOpenRenameDevice] = React.useState<FullDevice>();
    const [openExpireKey, setOpenExpireKey] = React.useState<FullDevice>();
    const [openDeleteDevice, setOpenDeleteDevice] = React.useState<FullDevice>();

    const form = useForm({
        defaultValues: {
            search: search,
        },
    });

    useEffect(() => {
        const listener = WS.addEventListener("message.DevicesChanged", () => {
            router.invalidate({ sync: true });
        });

        return () => WS.removeEventListener(listener);
    }, []);

    const columns = useDeviceColumns({
        setOpenRenameDevice,
        setOpenDeleteDevice,
        setOpenExpireKey,
    });
    const { table, isGrouped, setGrouping } = useDataTable({
        data: devices.items,
        columns,
        initialSorting: [{ id: "device-name", desc: false }],
        groupBy: ["tenant"],
        pageIndex: page - 1,
        onPageChange: (pageIndex) => navigate({ search: (prev) => ({ ...prev, page: pageIndex + 1 }), replace: true }),
    });

    // Filtering is fully client-side: the `search` and `filter` URL params are
    // the source of truth, and these effects push them into the table. The
    // loader has no `loaderDeps`, so updating them never re-fetches the data.
    // Page resets happen at the interaction sites (navigating with `page: 1`)
    // instead of here, so a deep-linked page isn't wiped on mount.
    const setTypeFilter = (f: Filter) =>
        navigate({ search: (prev) => ({ ...prev, filter: f, page: 1 }), replace: true });

    useEffect(() => {
        table.getColumn("tenant")?.setFilterValue(tenant === "all" ? undefined : tenant);
    }, [tenant]);

    useEffect(() => {
        table
            .getColumn("device-name")
            ?.setFilterValue(filter === "server-only" ? true : filter === "clients-only" ? false : undefined);
        table.getColumn("last-seen")?.setFilterValue(filter === "online-only" ? true : undefined);
    }, [filter]);

    useEffect(() => {
        table.setGlobalFilter(search);
    }, [search]);

    const tenantItems = tenant === "all" ? devices.items : devices.items.filter((d) => d.tenant.uuid === tenant);
    const chipTotal = tenantItems.length;
    const chipServers = tenantItems.filter((d) => d.user === null).length;
    const chipClients = tenantItems.filter((d) => d.user !== null).length;
    const onlineClients = tenantItems.filter((d) => d.online).length;

    return (
        <HeadingLayout
            heading={t("heading.device-overview")}
            headingDescription={t("description.device-overview")}
            headingChildren={
                <PrimaryButton href={"/a/devices/create-server"}>
                    <ServerStackIcon />
                    {t("button.add-server")}
                </PrimaryButton>
            }
        >
            {/* Stat strip — gives a one-glance summary that survives filtering. */}
            <FilterTileGroup
                value={filter}
                onChange={setTypeFilter}
                aria-label={t("label.filter")}
                className={"grid grid-cols-2 gap-3 sm:grid-cols-4"}
            >
                <FilterTile
                    value={"all"}
                    icon={<Squares2X2Icon className={"size-5"} />}
                    label={t("label.total-devices")}
                    count={chipTotal}
                />
                <FilterTile
                    value={"server-only"}
                    icon={<ServerStackIcon className={"size-5"} />}
                    label={t("label.servers", { defaultValue: "Servers" })}
                    count={chipServers}
                />
                <FilterTile
                    value={"clients-only"}
                    icon={<ComputerDesktopIcon className={"size-5"} />}
                    label={t("label.clients-stat")}
                    count={chipClients}
                />
                <FilterTile
                    value={"online-only"}
                    icon={
                        <SignalIcon
                            className={clsx(
                                "size-5",
                                filter === "online-only" ? "text-white" : "text-(--color-success)",
                            )}
                        />
                    }
                    label={t("label.online-devices")}
                    count={onlineClients}
                />
            </FilterTileGroup>

            <div className={"flex flex-col gap-3 sm:flex-row sm:items-center"}>
                <div className={"flex-1"}>
                    <form.Field
                        name={"search"}
                        validators={{
                            onChangeAsync: async ({ fieldApi }) => {
                                await navigate({
                                    search: (prev) => ({ ...prev, search: fieldApi.state.value, page: 1 }),
                                    replace: true,
                                });
                            },
                            onChangeAsyncDebounceMs: 500,
                        }}
                    >
                        {(fieldApi) => (
                            <InputGroup>
                                <MagnifyingGlassIcon data-slot="icon" />
                                <Input
                                    value={fieldApi.state.value}
                                    onChange={(e) => fieldApi.handleChange(e.target.value)}
                                    placeholder={t("label.filter")}
                                    aria-label={t("label.filter")}
                                />
                            </InputGroup>
                        )}
                    </form.Field>
                </div>
                <div className={"sm:w-56"}>
                    <Listbox
                        aria-label={t("label.filter-by-tenant")}
                        value={tenant ?? "all"}
                        onChange={async (x: string) => {
                            await navigate({
                                search: (prev) => ({ ...prev, tenant: x === "all" ? undefined : x, page: 1 }),
                                replace: true,
                            });
                        }}
                    >
                        <ListboxOption value={"all"}>
                            <ListboxLabel>{t("label.all-tenants")}</ListboxLabel>
                        </ListboxOption>
                        {tenants.list.map((tenant) => (
                            <ListboxOption key={tenant.uuid} value={tenant.uuid}>
                                <ListboxLabel>{tenant.name}</ListboxLabel>
                            </ListboxOption>
                        ))}
                    </Listbox>
                </div>
                <ToggleButton active={isGrouped} onClick={() => setGrouping(!isGrouped)}>
                    <RectangleGroupIcon />
                    {t("label.group-by-tenant")}
                </ToggleButton>
            </div>

            {devices.items.length === 0 ? (
                <EmptyState
                    icon={<ServerStackIcon />}
                    title={tg("empty.no-results-title")}
                    description={tg("empty.no-results-description")}
                    action={
                        <PrimaryButton href={"/a/devices/create-server"}>
                            <ServerStackIcon />
                            {t("button.add-server")}
                        </PrimaryButton>
                    }
                />
            ) : (
                <HIGHLIGHT_CONTEXT.Provider value={{ searchTerm: table.getState().globalFilter }}>
                    <DataTable
                        table={table}
                        groupBy={isGrouped ? ["tenant"] : undefined}
                        rowHref={"/a/devices/$deviceId/general"}
                        rowParams={(x) => ({ deviceId: x.uuid })}
                    />
                </HIGHLIGHT_CONTEXT.Provider>
            )}

            <Suspense>
                <RenameDeviceDialog
                    device={openRenameDevice}
                    isOpen={!!openRenameDevice}
                    onClose={() => setOpenRenameDevice(undefined)}
                    onRename={() => {
                        router.invalidate({ sync: true });
                        setOpenRenameDevice(undefined);
                    }}
                />

                <ExpireDeviceKeyDialog
                    device={openExpireKey}
                    isOpen={!!openExpireKey}
                    onClose={() => setOpenExpireKey(undefined)}
                    onExpire={() => {
                        router.invalidate({ sync: true });
                        setOpenExpireKey(undefined);
                    }}
                />

                <DeleteDeviceDialog
                    isOpen={!!openDeleteDevice}
                    onClose={() => setOpenDeleteDevice(undefined)}
                    onDeletion={() => {
                        router.invalidate({ sync: true });
                        setOpenDeleteDevice(undefined);
                    }}
                    device={openDeleteDevice}
                />
            </Suspense>
        </HeadingLayout>
    );
}

/**
 * Type that defines the search parameter of this endpoint
 */
type DeviceOverviewSearch = {
    /** The search term that should be used */
    search: string;
    /** The type of filter to use */
    filter: Filter;
    /** The page to display */
    page: number;
    /** The tenant to filter by */
    tenant?: "all" | string;
};

export const Route = createFileRoute("/_admin/a/devices/")({
    component: DeviceOverview,
    validateSearch: (search: Record<string, unknown>): DeviceOverviewSearch => {
        const page = Number(search.page ?? 1);
        return {
            search: typeof search.search === "string" ? search.search : "",
            filter: FILTERS.includes(search.filter as Filter) ? (search.filter as Filter) : "all",
            page: Number.isInteger(page) && page > 0 ? page : 1,
            tenant: search.tenant === "all" ? "all" : typeof search.tenant === "string" ? search.tenant : "all",
        };
    },
    loader: async () => {
        const [devices, tenants] = await Promise.all([ApiInternal.devices.getAll(), ApiInternal.tenants.getAll()]);
        return { devices, tenants };
    },
});
